import { ReactNode, useMemo, useState } from 'react';
import {
  ChevronRight,
  Eye,
  EyeOff,
  FilePlus,
  FileMinus,
  FileEdit,
  PencilLine,
  Plus,
  Minus,
  PenSquare,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  ShopChangelogChange,
  ShopChangelogDoc,
  ShopChangelogFieldChange,
  ShopChangelogPage,
  ShopChangelogPageRename,
  ShopChangelogPagesEntriesChanged,
  ShopChangelogModifiedEntry,
} from '../../types/shopChangelog';
import { ItemIcon } from '../ItemIcon';
import { cn } from '../../utils/cn';
import { ShopChangelogEntryRow } from './ShopChangelogEntryRow';
import { MinecraftPageName } from './MinecraftPageName';
import { ShopChangelogPagePill } from './ShopChangelogPagePill';
import { formatPrice } from './priceFormat';

interface ShopChangelogDetailProps {
  doc: ShopChangelogDoc;
}

/**
 * Full diff body shown when a feed row is expanded. Renders pagesAdded /
 * pagesRemoved / pagesRenamed / pagesEntriesChanged sections, each gated on
 * being non-empty.
 */
export function ShopChangelogDetail({ doc }: ShopChangelogDetailProps) {
  const { changelog, clusterId } = doc;
  const change: ShopChangelogChange = changelog ?? {};

  const sections: ReactNode[] = [];

  if (change.pagesAdded?.length) {
    sections.push(
      <PagesAddedSection key="pagesAdded" pages={change.pagesAdded} clusterId={clusterId} />,
    );
  }
  if (change.pagesRemoved?.length) {
    sections.push(
      <PagesRemovedSection key="pagesRemoved" pages={change.pagesRemoved} clusterId={clusterId} />,
    );
  }
  if (change.pagesRenamed?.length) {
    sections.push(
      <PagesRenamedSection key="pagesRenamed" renames={change.pagesRenamed} clusterId={clusterId} />,
    );
  }
  if (change.pagesEntriesChanged?.length) {
    sections.push(
      <PagesEntriesChangedSection
        key="pagesEntriesChanged"
        pages={change.pagesEntriesChanged}
        clusterId={clusterId}
      />,
    );
  }

  if (sections.length === 0) {
    return <div className="text-sm text-glass-400 italic">Diff not available for this push.</div>;
  }

  return (
    <div className="space-y-5">
      {sections}
      <SessionInfo doc={doc} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: pagesAdded
// ────────────────────────────────────────────────────────────────────────────

function PagesAddedSection({ pages, clusterId }: { pages: ShopChangelogPage[]; clusterId: string }) {
  return (
    <section>
      <SectionHeader icon={<FilePlus className="w-4 h-4" />} tone="add">
        Pages added ({pages.length})
      </SectionHeader>
      <div className="space-y-3 mt-2">
        {pages.map((page) => (
          <div
            key={page.id}
            className="bg-green-500/5 border border-green-500/20 rounded-md p-3 space-y-2"
          >
            <PageHeader pageId={page.id} pageName={page.name} clusterId={clusterId} />
            {page.entries && page.entries.length > 0 && (
              <div className="space-y-1 mt-2">
                {page.entries.map((entry, i) => (
                  <ShopChangelogEntryRow
                    key={`${entry.slot}-${i}`}
                    entry={entry}
                    clusterId={clusterId}
                    variant="added"
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: pagesRemoved
// ────────────────────────────────────────────────────────────────────────────

function PagesRemovedSection({ pages, clusterId }: { pages: ShopChangelogPage[]; clusterId: string }) {
  return (
    <section>
      <SectionHeader icon={<FileMinus className="w-4 h-4" />} tone="remove">
        Pages removed ({pages.length})
      </SectionHeader>
      <div className="space-y-3 mt-2">
        {pages.map((page) => (
          <div
            key={page.id}
            className="bg-red-500/5 border border-red-500/20 rounded-md p-3 space-y-2"
          >
            <PageHeader pageId={page.id} pageName={page.name} clusterId={clusterId} />
            {page.entries && page.entries.length > 0 && (
              <div className="space-y-1 mt-2">
                {page.entries.map((entry, i) => (
                  <ShopChangelogEntryRow
                    key={`${entry.slot}-${i}`}
                    entry={entry}
                    clusterId={clusterId}
                    variant="removed"
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: pagesRenamed
// ────────────────────────────────────────────────────────────────────────────

function PagesRenamedSection({
  renames,
  clusterId,
}: {
  renames: ShopChangelogPageRename[];
  clusterId: string;
}) {
  return (
    <section>
      <SectionHeader icon={<PencilLine className="w-4 h-4" />} tone="rename">
        Pages renamed ({renames.length})
      </SectionHeader>
      <div className="space-y-3 mt-2">
        {renames.map((rename) => (
          <div
            key={rename.id}
            className="bg-yellow-500/5 border border-yellow-500/20 rounded-md p-3 space-y-2"
          >
            <div className="flex items-center gap-2">
              <ShopChangelogPagePill pageId={rename.id} />
            </div>
            <div className="space-y-1.5">
              {rename.fields.map((field, i) => (
                <FieldChangeRow key={`${field.field}-${i}`} change={field} clusterId={clusterId} isMcText />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: pagesEntriesChanged
// ────────────────────────────────────────────────────────────────────────────

function PagesEntriesChangedSection({
  pages,
  clusterId,
}: {
  pages: ShopChangelogPagesEntriesChanged[];
  clusterId: string;
}) {
  return (
    <section>
      <SectionHeader icon={<FileEdit className="w-4 h-4" />} tone="modify">
        Pages with entry changes ({pages.length})
      </SectionHeader>
      <div className="space-y-4 mt-2">
        {pages.map((page) => (
          <PagesEntriesChangedBlock key={page.pageId} page={page} clusterId={clusterId} />
        ))}
      </div>
    </section>
  );
}

function PagesEntriesChangedBlock({
  page,
  clusterId,
}: {
  page: ShopChangelogPagesEntriesChanged;
  clusterId: string;
}) {
  const renamed = !pageNamesEqual(page.pageNameBefore, page.pageNameAfter);

  return (
    <div className="bg-glass-100/5 border border-glass-200/15 rounded-md p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base font-semibold truncate">
            <MinecraftPageName text={page.pageNameAfter ?? page.pageNameBefore} clusterId={clusterId} />
          </span>
          <ShopChangelogPagePill pageId={page.pageId} />
        </div>
        {renamed && (
          <span className="inline-flex items-center gap-1 text-xs text-yellow-300/90">
            <PencilLine className="w-3 h-3" />
            renamed from{' '}
            <span className="text-glass-300">
              <MinecraftPageName text={page.pageNameBefore} clusterId={clusterId} strikethrough />
            </span>
          </span>
        )}
      </div>

      {page.added.length > 0 && (
        <SubBlock icon={<Plus className="w-3.5 h-3.5" />} tone="add" title={`Added (${page.added.length})`}>
          <div className="space-y-1">
            {page.added.map((entry, i) => (
              <ShopChangelogEntryRow
                key={`a-${entry.slot}-${i}`}
                entry={entry}
                clusterId={clusterId}
                variant="added"
              />
            ))}
          </div>
        </SubBlock>
      )}

      {page.removed.length > 0 && (
        <SubBlock icon={<Minus className="w-3.5 h-3.5" />} tone="remove" title={`Removed (${page.removed.length})`}>
          <div className="space-y-1">
            {page.removed.map((entry, i) => (
              <ShopChangelogEntryRow
                key={`r-${entry.slot}-${i}`}
                entry={entry}
                clusterId={clusterId}
                variant="removed"
              />
            ))}
          </div>
        </SubBlock>
      )}

      {page.modified.length > 0 && (
        <SubBlock
          icon={<PenSquare className="w-3.5 h-3.5" />}
          tone="modify"
          title={`Modified (${page.modified.length})`}
        >
          <div className="space-y-3">
            {page.modified.map((mod, i) => (
              <ModifiedEntryBlock key={`m-${mod.slot}-${i}`} mod={mod} clusterId={clusterId} />
            ))}
          </div>
        </SubBlock>
      )}
    </div>
  );
}

function ModifiedEntryBlock({ mod, clusterId }: { mod: ShopChangelogModifiedEntry; clusterId: string }) {
  return (
    <div className="space-y-1.5">
      <ShopChangelogEntryRow
        entry={mod.after}
        clusterId={clusterId}
        variant="modified-header"
        slotOverride={mod.slot}
      />
      <div className="ml-6 space-y-1">
        {mod.fields.length === 0 && (
          <div className="text-xs text-glass-400 italic">(no fine-grained field diff)</div>
        )}
        {mod.fields.map((field, i) => (
          <FieldChangeRow key={`${field.field}-${i}`} change={field} clusterId={clusterId} />
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Field change row (used for both renames and per-entry modifications)
// ────────────────────────────────────────────────────────────────────────────

function FieldChangeRow({
  change,
  clusterId,
  isMcText = false,
}: {
  change: ShopChangelogFieldChange;
  clusterId: string;
  isMcText?: boolean;
}) {
  return (
    <div className="flex items-start gap-2 text-xs font-mono leading-relaxed">
      <span className="text-accent-300 shrink-0">{change.field}:</span>
      <span className="line-through text-red-300/90 break-all">
        {renderFieldValue(change.field, change.before, clusterId, isMcText)}
      </span>
      <ChevronRight className="w-3 h-3 text-glass-500 shrink-0 mt-0.5" />
      <span className="text-green-300/90 break-all">
        {renderFieldValue(change.field, change.after, clusterId, isMcText)}
      </span>
    </div>
  );
}

function renderFieldValue(
  field: string,
  value: unknown,
  clusterId: string,
  isMcText: boolean,
): ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-glass-500 italic">null</span>;
  }

  // Page-level renames: name/description are MC text components.
  if (isMcText && (field === 'name' || field === 'description')) {
    return <MinecraftPageName text={value as any} clusterId={clusterId} />;
  }

  if (field === 'buyPrice' || field === 'sellPrice') {
    const p = formatPrice(value);
    return (
      <span
        className={cn(
          p.tone === 'disabled' && 'italic',
          p.tone === 'unknown' && 'opacity-70',
        )}
      >
        {p.label}
      </span>
    );
  }

  if (field === 'stack.id' && typeof value === 'string' && value.includes(':')) {
    return (
      <span className="inline-flex items-center align-middle">
        <ItemIcon displayMode="inline" clusterId={clusterId} itemId={value} className="w-4 h-4 object-contain" />
      </span>
    );
  }

  if (field === 'pageId') {
    return <span className="text-blue-300">{String(value)}</span>;
  }

  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return <span>{String(value)}</span>;
  }

  let s: string;
  try {
    s = JSON.stringify(value);
  } catch {
    s = String(value);
  }
  if (s.length > 80) s = `${s.slice(0, 77)}…`;
  return <span>{s}</span>;
}

// ────────────────────────────────────────────────────────────────────────────
// Reusable bits
// ────────────────────────────────────────────────────────────────────────────

const TONE_CLASSES = {
  add:    'text-green-300',
  remove: 'text-red-300',
  modify: 'text-yellow-300',
  rename: 'text-yellow-200',
} as const;

function SectionHeader({
  icon,
  tone,
  children,
}: {
  icon: ReactNode;
  tone: keyof typeof TONE_CLASSES;
  children: ReactNode;
}) {
  return (
    <h3 className={cn('inline-flex items-center gap-2 text-sm font-semibold', TONE_CLASSES[tone])}>
      {icon}
      {children}
    </h3>
  );
}

function SubBlock({
  icon,
  tone,
  title,
  children,
}: {
  icon: ReactNode;
  tone: keyof typeof TONE_CLASSES;
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className={cn('inline-flex items-center gap-1.5 text-xs font-semibold mb-1', TONE_CLASSES[tone])}>
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function PageHeader({
  pageId,
  pageName,
  clusterId,
}: {
  pageId: string;
  pageName?: ShopChangelogPage['name'];
  clusterId: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-semibold">
        <MinecraftPageName text={pageName} clusterId={clusterId} />
      </span>
      <ShopChangelogPagePill pageId={pageId} />
    </div>
  );
}

function pageNamesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'string' && typeof b === 'string') return a === b;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function SessionInfo({ doc }: { doc: ShopChangelogDoc }) {
  const { identity } = useAuth();
  const canRevealIp = useMemo(() => {
    if (import.meta.env.DEV) return true;
    if (!identity) return false;
    const groups = identity.raw?.custom?.groups || [];
    return groups.includes('SPY_Bans_Admin') || groups.includes('SPY_Bans_Superadmins');
  }, [identity]);

  return (
    <div className="border-t border-glass-200/15 pt-3">
      <h4 className="text-xs font-semibold text-glass-400 mb-2">Session</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono">
        {doc.ip && canRevealIp && <RevealableIp ip={doc.ip} />}
        <CopyableField label="Session" value={doc.sessionJti} />
        <div className="md:col-span-3">
          <span className="text-glass-400 mr-2">User-Agent:</span>
          <span className="text-glass-300 break-all" title={doc.userAgent}>
            {doc.userAgent}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * IP field. Only rendered if the viewer is allowed to see IPs (matches the
 * "Click to reveal" pattern used in PlayerDataView). Stays hidden until the
 * viewer actively clicks to reveal — this keeps IPs out of incidental
 * screenshots even by users who do have access.
 */
function RevealableIp({ ip }: { ip: string }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <span className="text-glass-400">IP:</span>
      {revealed ? (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void navigator.clipboard.writeText(ip);
            }}
            className="text-glass-200 hover:text-accent-300 transition-colors"
            title="Click to copy"
          >
            {ip}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setRevealed(false);
            }}
            className="text-glass-500 hover:text-glass-300"
            title="Hide IP"
          >
            <EyeOff className="w-3 h-3" />
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setRevealed(true);
          }}
          className="inline-flex items-center gap-1 glass-button-sm text-[11px] py-0.5 px-1.5"
        >
          <Eye className="w-3 h-3" />
          Click to reveal
        </button>
      )}
    </div>
  );
}

function CopyableField({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div>
      <span className="text-glass-400 mr-2">{label}:</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          void navigator.clipboard.writeText(value);
        }}
        className="text-glass-200 hover:text-accent-300 transition-colors"
        title="Click to copy"
      >
        {value}
      </button>
    </div>
  );
}
